import { createSlice } from '@reduxjs/toolkit'
import { hasAlarmExpired, createAlarmTimeForTimeOfDay } from '../../alarm/index.js'
import { storyPaths } from '../storyPaths.js'

const initialState = {
  isCoolingDown: false,
  nextAlarmTimestamp: null
}

// Every story path fires its own `endScene` action when the player finishes a
// scene. The global cooldown blocks all paths ("Bel is busy") until the
// *soonest* pending beat is ready, so we listen to all of those actions and
// always keep the earliest alarm.
const endSceneTypes = new Set(storyPaths.map(({ slice }) => slice.actions.endScene.type))

export const globalCooldown = createSlice({
  name: 'globalCooldown',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    timerTick: (state) => {
      if (state.nextAlarmTimestamp) {
        if (hasAlarmExpired(state.nextAlarmTimestamp)) {
          state.nextAlarmTimestamp = null
          state.isCoolingDown = false
        }
      }
    },
    // Debug only: expire the cooldown so the next `timerTick` lifts it.
    debugFireAlarm: (state) => {
      if (state.nextAlarmTimestamp) {
        state.nextAlarmTimestamp = Date.now() - 1
      }
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => endSceneTypes.has(action.type),
      (state, action) => {
        // The story path's `endScene` payload is the next `timeOfDay`. In free
        // play mode (and on the final beat) there's no delay so it's null, and
        // we don't cool down.
        const timeOfDay = action.payload
        if (!timeOfDay) return
        const alarm = createAlarmTimeForTimeOfDay(timeOfDay)
        // Only ever shorten the cooldown: if this beat is ready sooner than the
        // current alarm, use the sooner time; otherwise leave the existing one.
        if (state.nextAlarmTimestamp == null || alarm < state.nextAlarmTimestamp) {
          state.nextAlarmTimestamp = alarm
        }
        state.isCoolingDown = true
      }
    )
  }
})

export const { timerTick, debugFireAlarm } = globalCooldown.actions

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectIsCoolingDown = ({ globalCooldown: state }) => {
  return state.isCoolingDown
}

export default globalCooldown.reducer
